import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ImageUpload from "../../components/ImageUpload";
import SortableList, { DragHandle } from "../../components/SortableList";
import RichTextEditor from "../../components/RichTextEditor";

const BLANK = {
  slug: "", title_tr: "", title_en: "", title_de: "", summary: "", content: "",
  icon: "Compass", image: "", related_categories: [], related_products: [],
  seo_title: "", seo_description: "", order: 0, active: true,
};

export default function AdminOpeningGuide() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/admin/opening-guide").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const body = { ...form, order: Number(form.order || 0) };
    try {
      if (editingId) await api.put(`/admin/opening-guide/${editingId}`, body);
      else await api.post("/admin/opening-guide", body);
      toast.success("Kaydedildi"); setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Hata"); }
  };

  const del = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/admin/opening-guide/${id}`);
    toast.success("Silindi"); load();
  };

  const persistReorder = async (next) => {
    setRows(next);
    try {
      await api.put(`/admin/opening-guide/_reorder`, { ids: next.map((r) => r.id) });
      toast.success("Sıralama güncellendi");
    } catch { toast.error("Sıra kaydedilemedi"); load(); }
  };

  return (
    <div className="p-8 max-w-6xl" data-testid="admin-guide">
      <div className="flex items-center justify-between mb-6">
        <h1 className="heading-serif text-3xl font-semibold text-navy">Açılış Rehberi Adımları</h1>
        <Button onClick={() => { setForm(BLANK); setEditingId(null); setOpen(true); }} className="bg-gold hover:bg-[#c49d58]" data-testid="admin-guide-new">
          <Plus size={16} className="mr-1" /> Yeni Adım
        </Button>
      </div>
      <div className="bg-white rounded-xl overflow-hidden border border-gray-100">
        <div className="bg-softbg text-xs uppercase tracking-wider text-gray-500 px-3 py-3 grid grid-cols-[auto_1fr_1fr_80px_80px_120px] gap-3 items-center">
          <span></span><span>Adım</span><span>Slug</span><span className="text-center">Sıra</span><span className="text-center">Aktif</span><span className="text-right">İşlem</span>
        </div>
        <div className="p-2">
          <SortableList items={rows} setItems={persistReorder} keyOf={(r) => r.id}>
            {(r, dragProps) => (
              <div className="grid grid-cols-[auto_1fr_1fr_80px_80px_120px] gap-3 items-center bg-white border border-gray-100 rounded-md px-3 py-2 mb-2 hover:bg-softbg/60" data-testid={`admin-guide-row-${r.slug}`}>
                <DragHandle {...dragProps} />
                <span className="font-medium text-navy text-sm">{r.title_tr}</span>
                <span className="text-gray-500 text-xs">/{r.slug}</span>
                <span className="text-center text-sm">{r.order}</span>
                <span className="text-center">{r.active ? "●" : "○"}</span>
                <div className="text-right">
                  <button onClick={() => { setForm({ ...BLANK, ...r }); setEditingId(r.id); setOpen(true); }} className="p-1.5 text-gray-500 hover:text-teal" data-testid={`admin-guide-edit-${r.slug}`}><Pencil size={14} /></button>
                  <button onClick={() => del(r.id)} className="p-1.5 text-gray-500 hover:text-rose-600" data-testid={`admin-guide-delete-${r.slug}`}><Trash2 size={14} /></button>
                </div>
              </div>
            )}
          </SortableList>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto admin-scroll">
          <DialogHeader><DialogTitle className="heading-serif">{editingId ? "Adımı Düzenle" : "Yeni Adım"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Slug</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required /></div>
              <div><Label className="text-xs">Başlık (TR)</Label><Input value={form.title_tr} onChange={(e) => setForm({ ...form, title_tr: e.target.value })} required /></div>
            </div>
            <div><Label className="text-xs">Özet</Label><Textarea rows={2} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} /></div>
            <div><Label className="text-xs">İçerik</Label><RichTextEditor value={form.content} onChange={(html) => setForm({ ...form, content: html })} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Icon (Lucide)</Label><Input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} /></div>
              <div className="col-span-2"><ImageUpload value={form.image} onChange={(v) => setForm({ ...form, image: v })} label="Görsel" testId="guide-image" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Sıra</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">SEO Başlık</Label><Input value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} /></div>
              <div><Label className="text-xs">SEO Açıklama</Label><Input value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} /></div>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Aktif</label>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Vazgeç</Button>
              <Button type="submit" className="bg-gold hover:bg-[#c49d58]">Kaydet</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
